import React, { useContext, useEffect, useState, useRef, useCallback } from "react";
import { Task } from "../task/Task";
import { ITask } from "../../../../types/dashboard";
import { AppContext } from "../../../../appContext";
import { DashboardContext } from "../../DashboardContext";
import { getTaskItems, postTaskItem, putTaskItem, deleteTaskItem } from "../../../../api/taskApi";
import { triggerTaskReload, triggerCalendarReload, addTaskReloadListener, removeTaskReloadListener, addTaskCheckedListener, removeTaskCheckedListener, triggerTaskCheckedUpdate } from "../../../../utils/dataSyncTriggers";

import './TaskList.css';

interface ITaskListProps{
    taskList?: ITask[],
    setTaskList?: (setter: (last: ITask[])=>ITask[])=>void,
    selectedTaskIndex?: number,
    setSelectedTaskIndex?: (index: number)=>void,
    setActivePopup?: (page: string)=>void
}

export const TaskList = ({}: ITaskListProps)=>{
    const { translate } = useContext(AppContext);
    const dashboardContext = useContext(DashboardContext);
    const { selectedClient } = dashboardContext || {};
    const [taskList, setTaskList] = useState<ITask[]>([]);
    const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [filterByClient, setFilterByClient] = useState(false);
    const skipNextReloadRef = useRef(false);

    const loadTasks = useCallback(async () => {
        if (skipNextReloadRef.current) {
            skipNextReloadRef.current = false;
            return;
        }
        
        try {
            setLoading(true);
            const res = await getTaskItems();
            if (!res) {
                throw new Error('No response from server');
            }
            const data = await res.json();
            
            let dataArray: any[];
            const reservedKeys = ['message', 'error', 'status', 'success', 'code', 'Error', 'Message'];
            if (Array.isArray(data)) {
                dataArray = data;
            } else {
                dataArray = Object.entries(data)
                    .filter(([id]) => !reservedKeys.includes(id))
                    .map(([id, taskData]) => ({ [id]: taskData }));
            }
            
            const convertedTasks: ITask[] = dataArray.map((item: any) => {
                const id = Object.keys(item)[0];
                if (reservedKeys.includes(id)) return null;
                const taskData = item[id];
                if (!taskData || typeof taskData !== 'object') return null;
                
                const rawDatetime = taskData.Datetime || taskData.datetime;
                let datetime: number | undefined = undefined;
                if (rawDatetime !== undefined && rawDatetime !== null && rawDatetime !== '') {
                    if (typeof rawDatetime === 'number') {
                        datetime = rawDatetime;
                    } else {
                        const date = new Date(rawDatetime);
                        if (!isNaN(date.getTime())) {
                            datetime = Math.floor(date.getTime() / 1000);
                        }
                    }
                }
                
                return {
                    id: id,
                    title: taskData.title || taskData.text?.slice(0, 20) || 'Untitled',
                    text: taskData.text || taskData.body || taskData.description || '',
                    tags: taskData.tags || [],
                    checked: taskData.checked || false,
                    datetime: datetime,
                    hasDate: taskData.hasDate !== undefined ? taskData.hasDate : Boolean(datetime),
                    client: taskData.client,
                    sessionId: taskData.sessionId
                };
            });
            
            setTaskList(convertedTasks.filter(Boolean) as ITask[]);
        } catch (err) {
            console.error('Failed to load tasks:', err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        loadTasks();
    }, [loadTasks]);

    useEffect(() => {
        addTaskReloadListener(loadTasks);
        return () => {
            removeTaskReloadListener(loadTasks);
        };
    }, [loadTasks]);

    useEffect(() => {
        const handleTaskCheckedUpdate = (taskId: string, checked: boolean) => {
            setTaskList(prev => prev.map(t => t.id === taskId ? { ...t, checked } : t));
        };
        addTaskCheckedListener(handleTaskCheckedUpdate);
        return () => {
            removeTaskCheckedListener(handleTaskCheckedUpdate);
        };
    }, []);

    const handleAddTask = async () => {
        const newTask = {
            title: translate('tasks_new_task') || 'New Task',
            text: '',
            tags: [] as string[],
            checked: false,
            Datetime: Math.floor(Date.now() / 1000),
            hasDate: true,
            type: 'task',
            client: filterByClient && selectedClient?.id ? selectedClient.id : undefined
        };

        try {
            const response = await postTaskItem(newTask);
            const result = await response.json();
            console.log('TaskList postTaskItem response:', result);
            
            const reservedKeys = ['message', 'error', 'status', 'success', 'code', 'Error', 'Message'];
            const validKeys = Object.keys(result).filter(k => !reservedKeys.includes(k));
            const taskId = result.name || result.id || validKeys[0] || null;
            
            if (!taskId) {
                console.log('No valid task ID from server response, reloading tasks...');
                await loadTasks();
                return;
            }
            
            const savedTask: ITask = {
                ...newTask,
                id: taskId,
                datetime: newTask.Datetime
            };

            setTaskList(prev => [...prev, savedTask]);
            setSelectedTaskId(taskId);
            skipNextReloadRef.current = true;
            triggerTaskReload();
            triggerCalendarReload();
        } catch (err) {
            console.error('Failed to create task:', err);
        }
    };

    const handleSaveTask = async (taskId: string, updatedData: { 
        title: string; 
        text: string; 
        tags?: string[]; 
        datetime?: number; 
        hasDate?: boolean;
        client?: string;
        sessionId?: string;
    }) => {
        const task = taskList.find(t => t.id === taskId);
        const reservedKeys = ['message', 'error', 'status', 'success', 'code', 'Error', 'Message'];
        if (!task || !task.id || reservedKeys.includes(task.id)) {
            console.error('Cannot save task without valid ID, taskId:', taskId);
            return;
        }

        try {
            const updatedTask: ITask = {
                ...task,
                title: updatedData.title,
                text: updatedData.text,
                tags: updatedData.tags || [],
                datetime: updatedData.datetime,
                hasDate: updatedData.hasDate,
                client: updatedData.client,
                sessionId: updatedData.sessionId
            };

            const apiPayload = {
                title: updatedData.title,
                text: updatedData.text,
                tags: updatedData.tags || [],
                Datetime: updatedData.hasDate ? updatedData.datetime : undefined,
                hasDate: updatedData.hasDate,
                checked: task.checked,
                client: updatedData.client,
                sessionId: updatedData.sessionId,
                type: 'task'
            };

            await putTaskItem(apiPayload, task.id);
            
            setTaskList(prev => prev.map(t => t.id === taskId ? updatedTask : t));
        } catch (err) {
            console.error('Failed to save task:', err);
        }
    };

    const handleRemoveTask = async (taskId: string) => {
        const task = taskList.find(t => t.id === taskId);
        const reservedKeys = ['message', 'error', 'status', 'success', 'code', 'Error', 'Message'];
        if (!task || !task.id || reservedKeys.includes(task.id)) {
            console.error('Cannot delete task without valid ID');
            return;
        }

        try {
            await deleteTaskItem(task.id);
            setTaskList(prev => prev.filter(t => t.id !== taskId));
            setSelectedTaskId(null);
            skipNextReloadRef.current = true;
            triggerTaskReload();
            triggerCalendarReload();
        } catch (err) {
            console.error('Failed to delete task:', err);
        }
    };

    const handleToggleTask = (taskId: string) => {
        setSelectedTaskId(prev => prev === taskId ? null : taskId);
    };

    const handleCheckTask = async (taskId: string) => {
        const task = taskList.find(t => t.id === taskId);
        if (!task || !task.id) return;

        const newChecked = !task.checked;
        setTaskList(prev => prev.map(t => t.id === taskId ? { ...t, checked: newChecked } : t));
        triggerTaskCheckedUpdate(taskId, newChecked);

        try {
            await putTaskItem({
                title: task.title,
                text: task.text,
                tags: task.tags || [],
                Datetime: task.hasDate ? task.datetime : undefined,
                hasDate: task.hasDate,
                checked: newChecked,
                client: task.client,
                sessionId: task.sessionId,
                type: 'task'
            }, task.id);
        } catch (err) {
            console.error('Failed to update task checked state:', err);
            setTaskList(prev => prev.map(t => t.id === taskId ? { ...t, checked: !newChecked } : t));
            triggerTaskCheckedUpdate(taskId, !newChecked);
        }
    };

    // Filter tasks based on filterByClient state
    const filteredTaskList = filterByClient && selectedClient?.id
        ? taskList.filter(task => task.client === selectedClient.id)
        : taskList;

    return (
        <div className="dashboard_main_block dashboard_main_tasks">
            <div className="dashboard_main_block_head">
                <div className="taskList_title_wrapper">
                    <div className="dashboard_main_block_title">
                        {translate('tasks_title')}
                    </div>
                    {selectedClient && (
                        <div 
                            className={`taskList_filter_icon ${!filterByClient ? 'taskList_filter_icon--active' : ''}`}
                            onClick={() => setFilterByClient(!filterByClient)}
                            title={filterByClient 
                                ? translate('filter_show_client_tasks').replace('{name}', selectedClient.name)
                                : translate('filter_show_all_tasks')
                            }
                        />
                    )}
                </div>
                <div className="dashboard_main_block_head_right">
                    <div className="taskList_add_wrapper" onClick={handleAddTask}>
                        <div className="taskList_add_icon"></div>
                        <span className="taskList_add_text">{translate('tasks_add_new') || 'Add a new task'}</span>
                    </div>
                </div>
            </div>
            {loading ? (
                <div className="tasks_loading">{translate('common_loading') || 'Loading...'}</div>
            ) : (
                <div className="tasks_content">
                    <div className="tasks">
                        {filteredTaskList.map((task) => (
                            <Task 
                                key={task.id}
                                isOpen={task.id === selectedTaskId} 
                                data={task} 
                                onToggle={() => handleToggleTask(task.id!)} 
                                onCheck={() => handleCheckTask(task.id!)}
                                onRemove={() => handleRemoveTask(task.id!)}
                                onSave={(updatedData) => handleSaveTask(task.id!, updatedData)}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
